import { useEffect, useState, useCallback } from "react";
import api, { formatErr, errDetails, loadPublicBaseUrl, publicLink } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CurrencyInr, Package, Clock, ChartBar, MagnifyingGlass, ArrowsClockwise, Truck, Trash, Paperclip, CheckCircle, FloppyDisk, X } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import DeliveryUploadBox from "./DeliveryUploadBox";
import { useBackClose } from "@/hooks/use-back-close";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Local YYYY-MM-DD for "today" — matches the format used by the date input and backend `date` field.
const todayYMD = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function EditField({ label, testid, error, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <input
        {...props}
        data-testid={testid}
        aria-invalid={!!error}
        className={`w-full mt-1.5 h-11 px-3 rounded-md border bg-card text-sm focus:outline-none focus:ring-2 ${
          error ? "border-signal focus:ring-signal/40" : "border-border focus:ring-signal/40"
        }`}
      />
      {error && <span className="text-xs text-signal mt-1 block" data-testid={`${testid}-error`}>{error}</span>}
    </label>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid, onClick, active, className = "", stackedOnPhone = false }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
      className={`brut-card p-3 sm:p-5 flex ${stackedOnPhone ? "flex-col sm:flex-row" : ""} items-start gap-2.5 sm:gap-4 transition-transform active:scale-[0.98] sm:hover:-translate-y-0.5 ${
        clickable ? "cursor-pointer" : ""
      } ${active ? "ring-2 ring-signal shadow-lg sm:-translate-y-0.5" : ""} ${className}`}
    >
      <div className={`w-9 h-9 sm:w-11 sm:h-11 shrink-0 rounded-md flex items-center justify-center ${accent}`}>
        <Icon size={18} weight="duotone" color="#fff" className="sm:hidden" />
        <Icon size={22} weight="duotone" color="#fff" className="hidden sm:block" />
      </div>
      <div className="min-w-0 max-w-full">
        <div className="text-[10px] sm:text-xs uppercase tracking-wider sm:tracking-widest leading-tight text-muted-foreground sm:truncate">{label}</div>
        <div className="font-display font-bold text-xl sm:text-2xl mt-0.5 sm:mt-1 truncate">{value}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  // Receipt link ke liye server ka IP pehle se le aao (WhatsApp button pe turant chahiye)
  useEffect(() => { loadPublicBaseUrl(); }, []);
  const { user } = useAuth();
  // Sales Reps (role="user") cannot mark orders delivered or delete records —
  // those actions are Admin/Owner only. This flag hides the Deliver/Delete
  // controls (and the row-select checkboxes that only exist to bulk-delete)
  // for Sales Reps, everywhere on this page.
  const canManage = user?.role === "owner" || user?.role === "admin";
  const [stats, setStats] = useState({ total: 0, pending: 0, delivered: 0, total_amount: 0 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [date, setDate] = useState(todayYMD());
  const [statusFilter, setStatusFilter] = useState("pending"); // "all" | "pending" | "delivered" — driven by the KPI cards
  const [syncing, setSyncing] = useState(false);
  const [deliveringIds, setDeliveringIds] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  // True once WhatsApp has been opened for the current `confirm` row and
  // we're waiting for the user to say the message was actually sent —
  // delivery is only marked on the server after that second click.
  const [awaitingWhatsapp, setAwaitingWhatsapp] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [receiptRow, setReceiptRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ voucher_type: "", invoice_no: "", date: "", party_name: "", party_mobile: "", invoice_amount: "", salesman: "", transporter: "" });
  const [editErrors, setEditErrors] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const pageSize = 1000;

  // Phone/browser back button closes whichever dialog is open instead of
  // navigating the page away (which used to jump out of the app).
  useBackClose(bulkDeleteOpen, () => setBulkDeleteOpen(false));
  useBackClose(!!confirm, () => setConfirm(null));
  useBackClose(!!deleteConfirm, () => setDeleteConfirm(null));
  useBackClose(!!receiptRow, () => setReceiptRow(null));
  useBackClose(!!editRow, () => setEditRow(null));

  const load = useCallback(async (statusOverride) => {
    try {
      const effectiveStatus = statusOverride ?? statusFilter;
      const [s, l] = await Promise.all([
        api.get("/sales/stats"),
        api.get("/sales", {
          params: {
            status: effectiveStatus === "all" ? undefined : effectiveStatus,
            search,
            date,
            page,
            page_size: pageSize,
          },
        }),
      ]);
      setStats(s.data);
      setRows(l.data.items);
      setTotal(l.data.total);
      // Drop any selected invoices that no longer exist in the freshly loaded list.
      setSelected((prev) => {
        const invoiceNos = new Set(l.data.items.map((r) => r.invoice_no));
        return new Set(Array.from(prev).filter((inv) => invoiceNos.has(inv)));
      });
    } catch (e) { toast.error(formatErr(e)); }
  }, [search, date, statusFilter, page]);

  useEffect(() => {
    load();

    const interval = setInterval(() => {
      load();
    }, 3000);

    return () => clearInterval(interval);
  }, [load]);

  // Clear selection whenever the visible list changes shape (filter, search, date, or page change).
  useEffect(() => { setSelected(new Set()); }, [search, date, statusFilter, page]);

  // Every time the deliver dialog opens for a (new) row, or closes, start
  // back at step 1 ("Open WhatsApp") rather than carrying over a stale
  // "waiting to confirm" state from a previous attempt.
  useEffect(() => { setAwaitingWhatsapp(false); }, [confirm]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/now");
      if (data.status === "success") toast.success(`Sync OK — ${data.added} added, ${data.duplicates} duplicates`);
      else if (data.status === "partial") toast.info(`Sync partial — 0 records`);
      else toast.error(`Sync failed: ${data.errors?.[0] || "unknown"}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSyncing(false); }
  };

  // Step 1: open WhatsApp with the delivery message pre-filled. Does NOT mark
  // the order delivered — that only happens in step 2, after the user has
  // actually sent the message and comes back to confirm it.
  const openWhatsapp = (e) => {
    // Stop AlertDialogAction's built-in "click closes the dialog" behavior —
    // this button only opens WhatsApp and switches to step 2, it should not
    // close the dialog yet.
    e.preventDefault();
    if (!confirm) return;

    // window.open must run synchronously inside the click handler (before any
    // await), otherwise browsers block it as a popup.
    const digits = (confirm.party_mobile || "").replace(/\D/g, "");
    const withCountryCode = digits.length === 10 ? `91${digits}` : digits; // assumes India (+91) for 10-digit numbers
    let message = `Dear ${confirm.party_name}, your material against Bill No - ${confirm.invoice_no} dated ${confirm.date} has been dispatched.`;
    if (confirm.receipt_file_path) {
      const receiptUrl = publicLink(confirm.receipt_file_path);
      message += `\nReceipt: ${receiptUrl}`;
    }
    window.open(`https://wa.me/${withCountryCode}?text=${encodeURIComponent(message)}`, "_blank");
    setAwaitingWhatsapp(true);
  };

  // Step 2: user has sent the WhatsApp message and confirms it — only now is
  // the order actually marked delivered on the server.
  const doDeliver = async () => {
    if (!confirm) return;

    const deliveredId = confirm.id;
    setBusy(true);
    try {
      await api.post(`/sales/${confirm.id}/deliver`);
      toast.success(`Marked as delivered — WhatsApp opened for ${confirm.party_name}`);
      setConfirm(null);
      // Dashboard is the "pending deliveries at a glance" view — once a sale is
      // delivered it belongs on the Delivered Orders page instead. Snap the view
      // back to "Pending" (in case Total Sales / Delivered was active) so the
      // reload below can't bring the row straight back, then fade it out.
      setStatusFilter("pending");
      setDeliveringIds((prev) => new Set(prev).add(deliveredId));
      setTimeout(() => {
        setDeliveringIds((prev) => {
          const next = new Set(prev);
          next.delete(deliveredId);
          return next;
        });
        load("pending"); // explicit override — avoids a stale closure re-fetching with the pre-deliver filter
      }, 320);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/sales/${encodeURIComponent(deleteConfirm.id)}`);
      toast.success(`Invoice ${deleteConfirm.invoice_no} deleted`);
      setDeleteConfirm(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setDeleting(false); }
  };

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.invoice_no));
  const someSelected = rows.some((r) => selected.has(r.invoice_no));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.invoice_no));
      return next;
    });
  };

  const toggleOne = (invoice_no) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(invoice_no)) next.delete(invoice_no);
      else next.add(invoice_no);
      return next;
    });
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const invoice_numbers = Array.from(selected);
      const { data } = await api.post("/sales/bulk-delete", { invoice_numbers });
      toast.success(`Deleted ${data.deleted} record${data.deleted === 1 ? "" : "s"}`);
      setBulkDeleteOpen(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBulkDeleting(false); }
  };

  const openEdit = (r) => {
    setEditRow(r);
    setEditForm({
      voucher_type: r.voucher_type,
      invoice_no: r.invoice_no,
      date: r.date,
      party_name: r.party_name,
      party_mobile: r.party_mobile,
      invoice_amount: r.invoice_amount,
      salesman: r.salesman || "",
      transporter: r.transporter || "",
    });
    setEditErrors({});
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.voucher_type.trim()) e.voucher_type = "Voucher type is required";
    if (!editForm.invoice_no.trim()) e.invoice_no = "Invoice number is required";
    if (!editForm.party_name.trim()) e.party_name = "Party name is required";
    if (!/^\d{10,}$/.test(String(editForm.party_mobile).replace(/\D/g, ""))) e.party_mobile = "Mobile must be at least 10 digits";
    if (!(Number(editForm.invoice_amount) > 0)) e.invoice_amount = "Invoice amount must be greater than 0";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveEdit = async () => {
    if (!editRow) return;
    if (!validateEdit()) return;
    setEditBusy(true);
    try {
      const { data } = await api.put(`/sales/${editRow.id}`, {
        voucher_type: editForm.voucher_type.trim(),
        invoice_no: editForm.invoice_no.trim(),
        date: editForm.date,
        party_name: editForm.party_name.trim(),
        party_mobile: editForm.party_mobile.trim(),
        invoice_amount: Number(editForm.invoice_amount),
        salesman: editForm.salesman.trim() || null,
        transporter: editForm.transporter.trim() || null,
      });
      toast.success(`Invoice ${data.invoice_no} updated`);
      setEditRow(null);
      load();
    } catch (e) {
      if (e?.response?.status === 422) {
        const fieldErrors = errDetails(e)?.errors || [];
        if (fieldErrors.length) {
          setEditErrors((prev) => ({ ...prev, ...Object.fromEntries(fieldErrors.map((f) => [f.field, f.message])) }));
        }
      }
      toast.error(formatErr(e));
    }
    finally { setEditBusy(false); }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Pending deliveries at a glance.</p>
        </div>
        <button
          onClick={doSync}
          disabled={syncing}
          data-testid="sync-now-button"
          className="signal-btn h-11 sm:h-10 px-4 rounded-md inline-flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-60 w-full sm:w-auto"
        >
          <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin-slow" : ""} />
          {syncing ? "Syncing..." : "Sync Now"}
        </button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <StatCard
          icon={ChartBar} label="Total Sales" value={stats.total} accent="bg-[#111]" testid="stat-total" stackedOnPhone
          onClick={() => { setStatusFilter("all"); setPage(1); }}
          active={statusFilter === "all"}
        />
        <StatCard
          icon={Clock} label="Pending" value={stats.pending} accent="bg-signal" testid="stat-pending" stackedOnPhone
          onClick={() => { setStatusFilter("pending"); setPage(1); }}
          active={statusFilter === "pending"}
        />
        <StatCard
          icon={Package} label="Delivered" value={stats.delivered} accent="bg-signal" testid="stat-delivered" stackedOnPhone
          onClick={() => { setStatusFilter("delivered"); setPage(1); }}
          active={statusFilter === "delivered"}
        />
        <StatCard icon={CurrencyInr} label="Total Invoice Amount" value={inr(stats.total_amount)} accent="bg-slate-700" testid="stat-amount" className="col-span-3 sm:col-span-1" />
      </div>

      <div className="brut-card">
        <div className="p-3 sm:p-4 border-b border-border flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2.5 sm:gap-3">
          <div className="w-full sm:flex-1 sm:min-w-[220px] relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by Invoice No, Voucher Type, Party or Mobile"
              data-testid="sales-search-input"
              className="w-full h-11 sm:h-10 pl-9 pr-3 rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-signal/40 text-sm"
            />
          </div>
          <div className="flex items-center gap-2.5 sm:gap-3">
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setPage(1); }}
              data-testid="sales-date-filter"
              className="flex-1 sm:flex-none h-11 sm:h-10 px-3 rounded-md border border-border bg-background text-sm"
            />

            <button
              type="button"
              onClick={() => { setSearch(""); setDate(""); setPage(1); }}
              className="shrink-0 h-11 sm:h-10 px-4 sm:px-3 rounded-md border border-border text-sm font-medium hover:bg-muted active:bg-muted"
              data-testid="clear-filters-button"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-xs uppercase tracking-[0.05em] text-muted-foreground">
                {canManage && (
                  <th className="text-left px-4 py-3 font-semibold w-10">
                    <Checkbox
                      checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      disabled={rows.length === 0}
                      aria-label="Select all rows on this page"
                      data-testid="sales-select-all-checkbox"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 font-semibold">Sl</th>
                <th className="text-left px-4 py-3 font-semibold">Voucher Type</th>
                <th className="text-left px-4 py-3 font-semibold">Invoice No</th>
                <th className="text-left px-4 py-3 font-semibold">Salesman</th>
                <th className="text-left px-4 py-3 font-semibold">Transporter</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Party</th>
                <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                <th className="text-right px-4 py-3 font-semibold">Invoice Amount</th>
                <th className="text-center px-4 py-3 font-semibold">Delivered</th>
                <th className="text-center px-4 py-3 font-semibold">Receipt</th>
                {canManage && <th className="text-right px-4 py-3 font-semibold">Action</th>}
              </tr>
            </thead>
            <tbody data-testid="sales-table-body">
              {rows.length === 0 && (
                <tr><td colSpan={canManage ? 13 : 11} className="text-center py-16 text-muted-foreground">
                  <Package size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
                  No pending deliveries
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr
                  key={r.id}
                  data-testid={`sale-row-${r.invoice_no}`}
                  className={`border-t border-border hover:bg-muted/40 transition-all duration-300 ${
                    deliveringIds.has(r.id) ? "opacity-0 -translate-x-2" : "opacity-100"
                  } ${selected.has(r.invoice_no) ? "bg-signal/5" : ""}`}
                >
                  {canManage && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(r.invoice_no)}
                        onCheckedChange={() => toggleOne(r.invoice_no)}
                        aria-label={`Select invoice ${r.invoice_no}`}
                        data-testid={`sales-row-checkbox-${r.invoice_no}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 text-muted-foreground">{(page - 1) * pageSize + i + 1}</td>
                  <td className="px-4 py-3 font-medium">
                    <span className="cursor-pointer" onClick={() => openEdit(r)}>{r.voucher_type}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <span className="cursor-pointer" onClick={() => openEdit(r)}>{r.invoice_no}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="cursor-pointer" onClick={() => openEdit(r)}>
                      {r.salesman || <span className="text-muted-foreground">—</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="cursor-pointer" onClick={() => openEdit(r)}>
                      {r.transporter || <span className="text-muted-foreground">—</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <span className="cursor-pointer" onClick={() => openEdit(r)}>{r.date}</span>
                  </td>
                  <td className="px-4 py-3">{r.party_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <span className="cursor-pointer" onClick={() => openEdit(r)}>{r.party_mobile}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{inr(r.invoice_amount)}</td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {r.delivery_status === "Yes" ? (
                      <span
                        data-testid={`status-badge-${r.invoice_no}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-signal/40 bg-signal/10 text-signal dark:text-signal"
                      >
                        Yes
                      </span>
                    ) : canManage ? (
                      <button
                        type="button"
                        onClick={() => setConfirm(r)}
                        data-testid={`status-badge-${r.invoice_no}`}
                        title="Click to mark as delivered"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-signal/40 bg-signal/10 text-signal dark:text-signal hover:bg-signal/20 hover:border-signal/60 transition-colors cursor-pointer"
                      >
                        No
                      </button>
                    ) : (
                      <span
                        data-testid={`status-badge-${r.invoice_no}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-border bg-muted text-muted-foreground"
                      >
                        No
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.receipt_file_path ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setReceiptRow(r); }}
                        data-testid={`view-receipt-btn-${r.invoice_no}`}
                        className="inline-flex items-center gap-1 text-xs text-signal hover:underline"
                      >
                        <CheckCircle size={14} weight="fill" /> Uploaded
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setReceiptRow(r); }}
                        data-testid={`upload-receipt-btn-${r.invoice_no}`}
                        className="signal-btn h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5"
                      >
                        <Paperclip size={14} weight="bold" /> Upload
                      </button>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {r.delivery_status === "Yes" ? (
                          <span
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`deliver-btn-${r.invoice_no}`}
                            title="Already delivered"
                            className="h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 border border-border bg-muted text-muted-foreground cursor-not-allowed"
                          >
                            <Truck size={14} weight="bold" /> Delivered
                          </span>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirm(r); }}
                            data-testid={`deliver-btn-${r.invoice_no}`}
                            className="signal-btn h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5"
                          >
                            <Truck size={14} weight="bold" /> Deliver
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(r); }}
                          data-testid={`delete-btn-${r.invoice_no}`}
                          className="h-8 px-2.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 border border-signal text-signal hover:bg-signal dark:hover:bg-signal/30"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list — shown below md breakpoint instead of the table */}
        <div className="md:hidden p-3 space-y-2.5 bg-muted/30">
          {rows.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <Package size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
              No pending deliveries
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`sale-card-${r.invoice_no}`}
              className={`rounded-lg border border-border bg-card p-4 space-y-3 shadow-sm transition-all duration-300 ${
                deliveringIds.has(r.id) ? "opacity-0 -translate-x-2" : "opacity-100"
              } ${selected.has(r.invoice_no) ? "ring-1 ring-signal/50 bg-signal/5" : ""}`}
            >
              <div className="space-y-1.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {canManage && (
                      <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                        <Checkbox
                          checked={selected.has(r.invoice_no)}
                          onCheckedChange={() => toggleOne(r.invoice_no)}
                          aria-label={`Select invoice ${r.invoice_no}`}
                          data-testid={`sales-row-checkbox-mobile-${r.invoice_no}`}
                        />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold leading-snug break-words line-clamp-2">{r.party_name}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-semibold whitespace-nowrap">{inr(r.invoice_amount)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 cursor-pointer whitespace-nowrap" onClick={() => openEdit(r)}>{r.date}</div>
                  </div>
                </div>
                <div className={`text-xs text-muted-foreground flex items-center flex-wrap gap-x-2 gap-y-1 ${canManage ? "pl-7" : ""}`}>
                  <span className="font-mono cursor-pointer whitespace-nowrap" onClick={() => openEdit(r)}>{r.invoice_no}</span>
                  <span className="cursor-pointer whitespace-nowrap px-1.5 py-0.5 rounded bg-muted text-[11px]" onClick={() => openEdit(r)}>{r.voucher_type}</span>
                </div>
              </div>

              <div className="h-px bg-border" />

              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Mobile</div>
                  <div className="font-mono text-[13px] cursor-pointer" onClick={() => openEdit(r)}>{r.party_mobile}</div>
                </div>
                {r.salesman && (
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Salesman</div>
                    <div className="text-[13px] leading-snug break-words">{r.salesman}</div>
                  </div>
                )}
                {r.transporter && (
                  <div className="col-span-2 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Transporter</div>
                    <div className="text-[13px] leading-snug break-words">🚚 {r.transporter}</div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {r.delivery_status === "Yes" ? (
                  <span
                    data-testid={`status-badge-mobile-${r.invoice_no}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-signal/40 bg-signal/10 text-signal dark:text-signal"
                  >
                    Delivered
                  </span>
                ) : canManage ? (
                  <button
                    type="button"
                    onClick={() => setConfirm(r)}
                    data-testid={`status-badge-mobile-${r.invoice_no}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-signal/40 bg-signal/10 text-signal dark:text-signal"
                  >
                    Pending
                  </button>
                ) : (
                  <span
                    data-testid={`status-badge-mobile-${r.invoice_no}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-border bg-muted text-muted-foreground"
                  >
                    Pending
                  </span>
                )}
                {r.receipt_file_path ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); setReceiptRow(r); }}
                    data-testid={`view-receipt-btn-mobile-${r.invoice_no}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-signal"
                  >
                    <CheckCircle size={14} weight="fill" /> Uploaded
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setReceiptRow(r); }}
                    data-testid={`upload-receipt-btn-mobile-${r.invoice_no}`}
                    className="signal-btn h-9 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5"
                  >
                    <Paperclip size={14} weight="bold" /> Upload
                  </button>
                )}
              </div>

              {canManage && (
                <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                  {r.delivery_status === "Yes" ? (
                    <span
                      data-testid={`deliver-btn-mobile-${r.invoice_no}`}
                      className="h-10 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 flex-1 justify-center border border-border bg-muted text-muted-foreground cursor-not-allowed"
                    >
                      <Truck size={14} weight="bold" /> Delivered
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirm(r)}
                      data-testid={`deliver-btn-mobile-${r.invoice_no}`}
                      className="signal-btn h-10 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 flex-1 justify-center"
                    >
                      <Truck size={14} weight="bold" /> Deliver
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(r)}
                    data-testid={`delete-btn-mobile-${r.invoice_no}`}
                    className="h-10 w-10 shrink-0 rounded-md text-xs font-semibold inline-flex items-center justify-center border border-signal text-signal"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-border flex flex-wrap items-center justify-between gap-y-2 gap-x-3 text-sm">
          <span className="text-muted-foreground">{total} pending records</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="prev-page-button"
              className="h-8 px-3 rounded-md border border-border disabled:opacity-40 hover:bg-muted"
            >Prev</button>
            <span className="font-mono text-xs whitespace-nowrap">Page {page} / {pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              data-testid="next-page-button"
              className="h-8 px-3 rounded-md border border-border disabled:opacity-40 hover:bg-muted"
            >Next</button>
          </div>
        </div>
      </div>

      {/* Floating bulk action bar — appears once 1+ rows are selected */}
      {canManage && selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 px-5 py-3 rounded-md border border-border bg-card shadow-lg max-w-[calc(100vw-2rem)] flex-wrap justify-center"
          data-testid="sales-bulk-action-bar"
        >
          <span className="text-sm font-medium">
            {selected.size} item{selected.size === 1 ? "" : "s"} selected
          </span>
          <button
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="sales-bulk-delete-button"
            className="h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
          >
            <Trash size={15} weight="bold" /> Delete Selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            data-testid="sales-bulk-clear-button"
            aria-label="Clear selection"
            className="h-9 w-9 rounded-md border border-border inline-flex items-center justify-center hover:bg-muted"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !o && setBulkDeleteOpen(false)}>
        <AlertDialogContent data-testid="sales-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Records</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selected.size}</strong> selected record{selected.size === 1 ? "" : "s"}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sales-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              disabled={bulkDeleting}
              data-testid="sales-bulk-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? "Deleting..." : "Confirm Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deliver via WhatsApp confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="deliver-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {awaitingWhatsapp ? "Confirm Message Sent" : "Confirm Delivery"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {awaitingWhatsapp ? (
                <>
                  WhatsApp has been opened for <strong>{confirm?.party_name}</strong>. Once you've
                  actually sent the message, click below to mark Invoice {confirm?.invoice_no} as delivered.
                </>
              ) : (
                <>
                  Are you sure this material has been delivered to <strong>{confirm?.party_name}</strong> (Invoice {confirm?.invoice_no})?
                  WhatsApp will open so you can notify {confirm?.party_mobile}.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deliver-cancel-button">Cancel</AlertDialogCancel>
            {awaitingWhatsapp ? (
              <AlertDialogAction onClick={doDeliver} disabled={busy} data-testid="deliver-confirm-button" className="signal-btn">
                {busy ? "Saving..." : "I've Sent It — Mark Delivered"}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction onClick={openWhatsapp} data-testid="deliver-open-whatsapp-button" className="signal-btn">
                Open WhatsApp
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete invoice <strong>{deleteConfirm?.invoice_no}</strong> ({deleteConfirm?.party_name}).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              data-testid="delete-confirm-button"
              className="bg-signal hover:bg-signal text-white"
            >
              {deleting ? "Deleting..." : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt upload / delivery note dialog */}
      <Dialog open={!!receiptRow} onOpenChange={(o) => !o && setReceiptRow(null)}>
        <DialogContent data-testid="receipt-upload-dialog">
          <DialogHeader>
            <DialogTitle>Delivery Receipt — {receiptRow?.invoice_no}</DialogTitle>
            <DialogDescription>
              Upload the delivery receipt / proof for <strong>{receiptRow?.party_name}</strong> and add a note.
            </DialogDescription>
          </DialogHeader>
          {receiptRow && (
            <DeliveryUploadBox
              saleId={receiptRow.id}
              invoiceNo={receiptRow.invoice_no}
              deliveryStatus={receiptRow.delivery_status}
              receiptFilePath={receiptRow.receipt_file_path}
              onConfirmed={(updatedSale) => {
                // Dialog open rehta hai taaki upload ke baad image preview dikhe
                if (updatedSale) setReceiptRow(updatedSale);
                load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Sale modal */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent data-testid="edit-sale-dialog" className="max-w-2xl max-h-[calc(100dvh-1.5rem)] overflow-y-auto max-sm:p-4 max-sm:pb-0 max-sm:gap-3">
          <DialogHeader>
            <DialogTitle>Edit Sale — {editRow?.invoice_no}</DialogTitle>
            <DialogDescription>Update this invoice's details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-sm:gap-3">
            <div className="md:col-span-2">
              <EditField
                label="Voucher Type"
                value={editForm.voucher_type}
                onChange={(e) => setEditForm((f) => ({ ...f, voucher_type: e.target.value }))}
                testid="edit-voucher-type-input"
                error={editErrors.voucher_type}
              />
            </div>
            <EditField
              label="Invoice No"
              value={editForm.invoice_no}
              onChange={(e) => setEditForm((f) => ({ ...f, invoice_no: e.target.value }))}
              testid="edit-invoice-no-input"
              error={editErrors.invoice_no}
            />
            <EditField
              label="Date"
              type="date"
              value={editForm.date}
              onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
              testid="edit-date-input"
            />
            <EditField
              label="Party Name"
              value={editForm.party_name}
              onChange={(e) => setEditForm((f) => ({ ...f, party_name: e.target.value }))}
              testid="edit-party-name-input"
              error={editErrors.party_name}
            />
            <EditField
              label="Party Mobile"
              value={editForm.party_mobile}
              onChange={(e) => setEditForm((f) => ({ ...f, party_mobile: e.target.value }))}
              testid="edit-party-mobile-input"
              error={editErrors.party_mobile}
            />
            <EditField
              label="Salesman"
              value={editForm.salesman}
              onChange={(e) => setEditForm((f) => ({ ...f, salesman: e.target.value }))}
              testid="edit-salesman-input"
              error={editErrors.salesman}
            />
            <EditField
              label="Transporter"
              value={editForm.transporter}
              onChange={(e) => setEditForm((f) => ({ ...f, transporter: e.target.value }))}
              testid="edit-transporter-input"
              error={editErrors.transporter}
            />
            <div className="md:col-span-2">
              <EditField
                label="Invoice Amount (₹)"
                type="number"
                min="0"
                step="0.01"
                value={editForm.invoice_amount}
                onChange={(e) => setEditForm((f) => ({ ...f, invoice_amount: e.target.value }))}
                testid="edit-invoice-amount-input"
                error={editErrors.invoice_amount}
              />
            </div>
          </div>
          <DialogFooter className="max-sm:sticky max-sm:bottom-0 max-sm:z-10 max-sm:bg-background max-sm:-mx-4 max-sm:px-4 max-sm:pb-4 max-sm:pt-3 max-sm:border-t max-sm:border-border max-sm:flex-row max-sm:gap-2">
            <button type="button" onClick={() => setEditRow(null)} className="h-10 px-4 rounded-md border border-border text-sm hover:bg-muted">
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={editBusy}
              data-testid="save-edit-sale-button"
              className="signal-btn h-10 px-5 rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 max-sm:flex-1 max-sm:justify-center"
            >
              <FloppyDisk size={16} weight="bold" /> {editBusy ? "Saving..." : "Save Changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}